import React, { useState, useEffect, useRef } from 'react';
import {
  Grid, FormControl, InputLabel, Select, MenuItem, OutlinedInput, Chip, Box, Typography,
  Stack, IconButton, Drawer, Divider, Alert, CircularProgress, Autocomplete, TextField,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DownloadIcon from '@mui/icons-material/Download';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { toast } from 'react-toastify';
import SchoolOutlinedIcon from '@mui/icons-material/SchoolOutlined';
import { DataTable, PillButton } from '../../../components/superAdminUi';
import SectionCard from '../../../components/common/SectionCard';
import SchoolFormFields from '../../../components/common/SchoolFormFields';
import schoolService from '../../../services/api/schoolService';
import { emptySchoolFormValues, prefillFromPrevious } from '../../../utils/schoolFormMapper';
import { rowChip } from './schoolsListUtils';
import {
  getBulkSchoolTemplateUrl,
  BULK_SCHOOL_TEMPLATE_DOWNLOAD_NAME,
} from '../../../constants/bulkSchoolTemplate';

// The manual slide-over runs the same inline duplicate check the standalone add does.
function useServerDuplicate(name, urn, active) {
  const [duplicate, setDuplicate] = useState(null);
  const timer = useRef(null);
  useEffect(() => {
    if (!active) return undefined;
    const n = (name || '').trim();
    const u = (urn || '').toString().trim();
    if (!n && !u) {
      setDuplicate(null);
      return undefined;
    }
    // Latch so a slow, superseded response can't overwrite the current one.
    let current = true;
    timer.current = setTimeout(() => {
      schoolService.checkSchoolDuplicate(n, u).then((result) => {
        if (current) setDuplicate(result && result.isDuplicate ? result : null);
      });
    }, 500);
    return () => {
      current = false;
      clearTimeout(timer.current);
    };
  }, [name, urn, active]);
  return duplicate;
}

// Step 3: one list, four intakes (GIAS multi-select, attach existing, manual slide-over,
// file dry-run). The unified list renders through the shared Super Admin DataTable.
export default function SchoolsStep({
  rows,
  giasOptions,
  existingSchoolOptions = [],
  customerTypes,
  onAddGias,
  onRemoveGias,
  onRemoveRow,
  onAddManual,
  onAttachExisting,
  onUpdateRow,
  onFileSelected,
  fileBusy,
}) {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState(emptySchoolFormValues());
  const fileInputRef = useRef(null);

  const serverDuplicate = useServerDuplicate(draft.Name, draft.Urn, drawerOpen && editingId === null);

  const selectedGiasUrns = rows
    .filter((row) => row.source === 'gias')
    .map((row) => String(row.values.Urn));

  const openNew = () => {
    setEditingId(null);
    setDraft(emptySchoolFormValues());
    setDrawerOpen(true);
  };

  const openEdit = (row) => {
    setEditingId(row.id);
    setDraft(row.values);
    setDrawerOpen(true);
  };

  const closeDrawer = () => {
    setDrawerOpen(false);
    setEditingId(null);
    setDraft(emptySchoolFormValues());
  };

  const setField = (name, value) => setDraft((prev) => ({ ...prev, [name]: value }));

  const validateDraft = () => {
    if (!draft.Name || draft.Name.trim() === '') {
      toast.error('Please enter School Name');
      return false;
    }
    if (!draft.Urn || draft.Urn.toString().trim() === '') {
      toast.error('Please enter School URN');
      return false;
    }
    if (editingId === null && serverDuplicate && serverDuplicate.isDuplicate) {
      toast.error(`A school with this ${serverDuplicate.matchedBy || 'name/URN'} already exists.`);
      return false;
    }
    return true;
  };

  const handleSave = () => {
    if (!validateDraft()) return;
    if (editingId !== null) {
      onUpdateRow(editingId, draft);
      closeDrawer();
      return;
    }
    if (onAddManual(draft)) {
      closeDrawer();
    }
  };

  const handleAddAnother = () => {
    if (!validateDraft()) return;
    if (onAddManual(draft)) {
      setDraft(prefillFromPrevious(draft));
    }
  };

  const handleGiasChange = (event) => {
    const nextUrns = (typeof event.target.value === 'string' ? event.target.value.split(',') : event.target.value).map(String);
    const added = nextUrns.filter((urn) => !selectedGiasUrns.includes(urn));
    const removed = selectedGiasUrns.filter((urn) => !nextUrns.includes(urn));
    added.forEach((urn) => {
      const option = giasOptions.find((o) => String(o.URN) === urn);
      if (option) onAddGias(option);
    });
    removed.forEach((urn) => onRemoveGias(urn));
  };

  const handleFilePick = (event) => {
    const file = event.target.files?.[0];
    if (file) onFileSelected(file);
    event.target.value = '';
  };

  const hasDuplicates = rows.some((row) => row.duplicate);

  // Unified schools list, rendered through the shared DataTable. Existing (attached) rows are
  // DB records and stay non-editable inline; a pinned server-side create error expands beneath
  // its row until the row is edited.
  const schoolColumns = [
    {
      key: 'name',
      label: 'School Name',
      width: '30%',
      cellStyle: { whiteSpace: 'normal' },
      render: (row) => row.values.Name || '(unnamed school)',
    },
    { key: 'urn', label: 'URN', width: '14%', render: (row) => row.values.Urn || 'N/A' },
    { key: 'source', label: 'Source', width: '14%', render: (row) => row.source },
    {
      key: 'status',
      label: 'Status',
      width: '18%',
      cellStyle: { whiteSpace: 'normal' },
      render: (row) => {
        const chip = rowChip(row);
        return <Chip size="small" color={chip.color} label={chip.label} />;
      },
    },
    {
      key: 'action',
      label: 'Action',
      align: 'right',
      width: '120px',
      render: (row) => (
        <Stack direction="row" spacing={0.5} justifyContent="flex-end">
          {/* Existing (attached) schools are DB records, not editable inline. */}
          {row.source !== 'existing' && (
            <IconButton size="small" onClick={() => openEdit(row)} title="Edit"><EditIcon fontSize="small" /></IconButton>
          )}
          <IconButton
            size="small"
            color="error"
            onClick={() => (row.source === 'gias' ? onRemoveGias(String(row.values.Urn)) : onRemoveRow(row.id))}
            title="Remove"
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Stack>
      ),
    },
  ];

  return (
    <SectionCard
      title="Schools"
      subtitle="Add schools from GIAS, manually, or from a file."
      icon={<SchoolOutlinedIcon fontSize="small" />}
    >
      <Grid container spacing={1.5}>
      {giasOptions.length > 0 && (
        <Grid item xs={12}>
          <FormControl fullWidth>
            <InputLabel id="register-group-gias-label">Select from GIAS</InputLabel>
            <Select
              labelId="register-group-gias-label"
              multiple
              value={selectedGiasUrns}
              onChange={handleGiasChange}
              input={<OutlinedInput label="Select from GIAS" />}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.map((urn) => {
                    const option = giasOptions.find((o) => String(o.URN) === urn);
                    return <Chip key={urn} label={option ? option.EstablishmentName : urn} />;
                  })}
                </Box>
              )}
            >
              {giasOptions.map((option) => (
                <MenuItem key={option.URN} value={String(option.URN)}>
                  <Box>
                    <Typography variant="body1">{option.EstablishmentName}</Typography>
                    <Typography variant="caption" color="text.secondary" display="block">
                      URN: {option.URN} | Pupils: {option.NumberOfPupils || 'N/A'} | {option.PhaseOfEducation_name || ''}
                    </Typography>
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
      )}

      {existingSchoolOptions.length > 0 && (
        <Grid item xs={12}>
          <Autocomplete
            options={existingSchoolOptions}
            value={null}
            blurOnSelect
            clearOnBlur
            getOptionLabel={(option) => option.Name || ''}
            isOptionEqualToValue={(option, value) => option.Id === value.Id}
            onChange={(_e, option) => { if (option && onAttachExisting) onAttachExisting(option); }}
            renderInput={(params) => <TextField {...params} label="Attach existing school" placeholder="Search existing Welbee schools" />}
          />
        </Grid>
      )}

      <Grid item xs={12}>
        <Stack direction="row" spacing={1.5} flexWrap="wrap">
          <PillButton type="button" $variant="danger" $size="lg" onClick={openNew}>
            <AddIcon sx={{ fontSize: 16 }} /> Add manually
          </PillButton>
          <PillButton
            type="button"
            $variant="outline"
            $size="lg"
            onClick={() => fileInputRef.current && fileInputRef.current.click()}
            disabled={fileBusy}
          >
            {fileBusy ? <CircularProgress size={18} /> : (<><UploadFileIcon sx={{ fontSize: 16 }} /> From file (.xlsx)</>)}
          </PillButton>
          <PillButton
            as="a"
            $variant="outline"
            $size="lg"
            href={getBulkSchoolTemplateUrl()}
            download={BULK_SCHOOL_TEMPLATE_DOWNLOAD_NAME}
          >
            <DownloadIcon sx={{ fontSize: 16 }} /> Download template
          </PillButton>
          <input ref={fileInputRef} type="file" hidden accept=".xlsx" onChange={handleFilePick} />
        </Stack>
      </Grid>

      {hasDuplicates && (
        <Grid item xs={12}>
          <Alert severity="error">Resolve the duplicate schools below before continuing.</Alert>
        </Grid>
      )}

      <Grid item xs={12}>
        {rows.length > 0 && (
          <Typography variant="subtitle2" sx={{ mb: 1 }}>Schools ({rows.length})</Typography>
        )}
        <DataTable
          columns={schoolColumns}
          rows={rows}
          getRowKey={(row) => row.id}
          empty="No schools added yet."
          isRowExpanded={(row) => Boolean(row.serverError)}
          renderExpanded={(row) => (
            <Box sx={{ p: 1.5 }}>
              {/* Server-side create errors stay pinned on their row until it is edited. */}
              <Alert severity="error">{row.serverError}</Alert>
            </Box>
          )}
        />
      </Grid>

      <Drawer anchor="right" open={drawerOpen} onClose={closeDrawer}>
        <Box sx={{ width: { xs: '100vw', sm: 520 }, p: 3 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>{editingId !== null ? 'Edit school' : 'Add school'}</Typography>

          {editingId === null && serverDuplicate && serverDuplicate.isDuplicate && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              A school with this {serverDuplicate.matchedBy || 'name/URN'} already exists
              {serverDuplicate.existingName ? `: ${serverDuplicate.existingName}` : ''}.
            </Alert>
          )}

          <SchoolFormFields
            values={draft}
            onFieldChange={setField}
            variant="compact"
            customerTypeOptions={customerTypes}
          />

          <Divider sx={{ my: 2 }} />
          <Stack direction="row" spacing={1.5} justifyContent="flex-end" flexWrap="wrap">
            <PillButton type="button" $variant="outline" $size="lg" onClick={closeDrawer}>Cancel</PillButton>
            {editingId === null && (
              <PillButton type="button" $variant="outline" $size="lg" onClick={handleAddAnother}>Add another like this</PillButton>
            )}
            <PillButton type="button" $variant="danger" $size="lg" onClick={handleSave}>Save</PillButton>
          </Stack>
        </Box>
      </Drawer>
      </Grid>
    </SectionCard>
  );
}
